/**
 * 🛡️ G2GENTE - ARQUIVO PROTEGIDO | SELO: G2GENTE-PROTECTED-2026-02-24-V1
 * MobileSolicitations - Solicitações de contato + Testes/Pesquisas pendentes
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { solicitationsAPI, pendingTestsAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft,
  Loader2,
  Clock,
  CheckCircle2,
  XCircle,
  Building2,
  Shield,
  Calendar,
  Brain,
  ClipboardList,
  AlertCircle,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { MyInterviewsList } from '../../components/interviews/MyInterviewsList';

const TIPO_TESTE_CONFIG = {
  disc: { label: 'DISC', color: 'bg-violet-100 text-violet-700', icon: '🧠' },
  bigfive: { label: 'Big Five', color: 'bg-blue-100 text-blue-700', icon: '🎯' },
  clima: { label: 'Pesquisa de Clima', color: 'bg-emerald-100 text-emerald-700', icon: '📊' }
};

export default function MobileSolicitations() {
  const navigate = useNavigate();
  const [solicitations, setSolicitations] = useState([]);
  const [pendingTests, setPendingTests] = useState([]);
  const [pendingSurveys, setPendingSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPending, setLoadingPending] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchSolicitations();
    fetchPendingTests();
  }, []);

  const fetchSolicitations = async () => {
    try {
      const response = await solicitationsAPI.getReceived();
      setSolicitations(response.data);
    } catch (error) {
      toast.error('Erro ao carregar solicitações');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingTests = async () => {
    try {
      const response = await pendingTestsAPI.getMyPending();
      setPendingTests(response.data.pending_tests || []);
      setPendingSurveys(response.data.pending_surveys || []);
    } catch (error) {
      console.error('Erro ao carregar testes pendentes:', error);
    } finally {
      setLoadingPending(false);
    }
  };

  const handleApprove = async (id) => {
    setProcessing(id);
    try {
      await solicitationsAPI.approve(id);
      toast.success('Acesso aprovado! Chat disponível.');
      fetchSolicitations();
    } catch (error) {
      toast.error('Erro ao aprovar');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (id) => {
    setProcessing(id);
    try {
      await solicitationsAPI.reject(id);
      toast.success('Solicitação rejeitada');
      fetchSolicitations();
    } catch (error) {
      toast.error('Erro ao rejeitar');
    } finally {
      setProcessing(null);
    }
  };

  const handleGoToTest = (test) => {
    // Redirecionar para a página de convite de teste
    navigate(`/m/test-invite/${test.token}`);
  };

  const handleGoToSurvey = (survey) => {
    // Redirecionar para a pesquisa de clima via link público
    window.location.href = `/p/clima/${survey.link_publico}`;
  };

  const getStatusBadge = (status, direto) => {
    if (status === 'APPROVED' && direto) return <Badge className="bg-cyan-100 text-cyan-700"><CheckCircle2 className="w-3 h-3 mr-1" />Selecionado pela empresa</Badge>;
    const badges = {
      PENDING: <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>,
      APPROVED: <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Aprovado</Badge>,
      REJECTED: <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Rejeitado</Badge>
    };
    return badges[status];
  };

  const pending = solicitations.filter(s => s.status === 'PENDING');
  const others = solicitations.filter(s => s.status !== 'PENDING');
  
  // Total de pendências
  const totalPendencias = pending.length + pendingTests.length + pendingSurveys.length;

  return (
    <div className="min-h-screen bg-slate-50 pb-6" data-testid="mobile-solicitations">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 border-b border-slate-200">
        <div className="flex items-center gap-3 p-4">
          <button onClick={() => navigate('/m')} className="text-slate-600">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-slate-900">Solicitações</h1>
            {totalPendencias > 0 && (
              <p className="text-xs text-amber-600">{totalPendencias} pendência(s)</p>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6">
        <MyInterviewsList />
        
        {/* Testes Comportamentais Pendentes */}
        {!loadingPending && pendingTests.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Brain className="w-5 h-5 text-violet-600" />
              <h2 className="font-semibold text-slate-900">
                Testes Comportamentais Solicitados
              </h2>
              <Badge className="bg-violet-100 text-violet-700 text-xs">
                {pendingTests.length}
              </Badge>
            </div>
            
            <div className="space-y-3">
              {pendingTests.map((test) => {
                const config = TIPO_TESTE_CONFIG[test.tipo_teste] || TIPO_TESTE_CONFIG.disc;
                return (
                  <Card 
                    key={test.id} 
                    className="border-violet-200 bg-gradient-to-r from-violet-50 to-white cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleGoToTest(test)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center text-2xl">
                          {config.icon}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={config.color}>{config.label}</Badge>
                            <Badge className="bg-amber-100 text-amber-700">
                              <Clock className="w-3 h-3 mr-1" />
                              Pendente
                            </Badge>
                          </div>
                          <p className="font-medium text-slate-900">
                            Solicitado por {test.company_name}
                          </p>
                          <p className="text-xs text-slate-500">
                            Expira em {new Date(test.expires_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Pesquisas de Clima Pendentes */}
        {!loadingPending && pendingSurveys.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <ClipboardList className="w-5 h-5 text-emerald-600" />
              <h2 className="font-semibold text-slate-900">
                Pesquisas de Clima da sua Empresa
              </h2>
              <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                {pendingSurveys.length}
              </Badge>
            </div>
            
            <div className="space-y-3">
              {pendingSurveys.map((survey) => (
                <Card 
                  key={survey.id} 
                  className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-white cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleGoToSurvey(survey)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center text-2xl">
                        📊
                      </div>
                      <div className="flex-1">
                        <Badge className="bg-emerald-100 text-emerald-700 mb-1">
                          Pesquisa de Clima
                        </Badge>
                        <p className="font-medium text-slate-900">
                          {survey.titulo}
                        </p>
                        <p className="text-xs text-slate-500">
                          {survey.company_name} • Respostas anônimas
                        </p>
                        {survey.data_fim && (
                          <p className="text-xs text-amber-600">
                            Até {new Date(survey.data_fim).toLocaleDateString('pt-BR')}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Separador se houver testes/pesquisas E solicitações */}
        {(pendingTests.length > 0 || pendingSurveys.length > 0) && solicitations.length > 0 && (
          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-5 h-5 text-cyan-600" />
              <h2 className="font-semibold text-slate-900">Solicitações de Contato</h2>
            </div>
          </div>
        )}

        {/* Info Banner */}
        {solicitations.length > 0 && (
          <div className="bg-cyan-50 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-cyan-600 mt-0.5" />
              <div>
                <p className="font-medium text-cyan-900 text-sm">Seus dados estão protegidos</p>
                <p className="text-xs text-cyan-700">
                  Empresas só podem ver seus dados completos após sua aprovação.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
          </div>
        ) : solicitations.length === 0 && pendingTests.length === 0 && pendingSurveys.length === 0 ? (
          <div className="text-center py-20">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-medium text-slate-900 mb-2">Nenhuma solicitação</h3>
            <p className="text-sm text-slate-600">
              Quando empresas quiserem acessar seu perfil ou solicitar testes, aparecerá aqui.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Pending Contact Requests */}
            {pending.length > 0 && (
              <div>
                <h2 className="font-semibold text-slate-900 mb-3">
                  Aguardando sua resposta ({pending.length})
                </h2>
                <div className="space-y-3">
                  {pending.map((solicitation) => (
                    <Card 
                      key={solicitation.id} 
                      className="border-amber-200 bg-amber-50"
                      data-testid={`mobile-solicitation-${solicitation.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                            <Building2 className="w-6 h-6 text-amber-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-slate-900">
                              {solicitation.company_name || 'Empresa'}
                            </h3>
                            {solicitation.job_title && (
                              <p className="text-sm text-cyan-600 font-medium">
                                Vaga: {solicitation.job_title}
                              </p>
                            )}
                            <p className="text-xs text-slate-600">
                              Quer acessar seu perfil completo
                            </p>
                            <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(solicitation.created_at).toLocaleDateString('pt-BR')}
                            </div>
                          </div>
                        </div>
                        
                        {/* Mensagem inicial da empresa */}
                        {solicitation.message && (
                          <div className="bg-white rounded-lg p-3 mb-3 border border-amber-200">
                            <p className="text-xs text-slate-500 mb-1">Mensagem da empresa:</p>
                            <p className="text-sm text-slate-700 italic">&ldquo;{solicitation.message}&rdquo;</p>
                          </div>
                        )}
                        
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => handleReject(solicitation.id)}
                            disabled={processing === solicitation.id}
                          >
                            {processing === solicitation.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              'Recusar'
                            )}
                          </Button>
                          <Button
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleApprove(solicitation.id)}
                            disabled={processing === solicitation.id}
                          >
                            {processing === solicitation.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              'Aprovar'
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* History Section */}
            {others.length > 0 && (
              <div>
                <h2 className="font-semibold text-slate-900 mb-3">Histórico</h2>
                <div className="space-y-3">
                  {others.map((solicitation) => (
                    <Card 
                      key={solicitation.id} 
                      className="border-slate-200"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-slate-400" />
                            </div>
                            <div>
                              <h3 className="font-medium text-slate-900">
                                {solicitation.company_name || 'Empresa'}
                              </h3>
                              <p className="text-xs text-slate-500">
                                {new Date(solicitation.created_at).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                          </div>
                          {getStatusBadge(solicitation.status, solicitation.acesso_direto)}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
